import { useShopI18n } from '../../i18n'
import { CustomerAuthErrorBanner } from './customer-auth'
import { buildFieldUiHelpers } from './customer-auth/CustomerAuthForm.helpers'
import {
  ForgotPasswordFormView,
  ForgotPasswordStateCard,
  useForgotPasswordFlowReducer,
  useForgotPasswordSubmitHandlers,
  useForgotPasswordViewModel,
} from './customer-auth/forgot-password'

export const CustomerForgotPasswordPage = () => {
  const { t, toLocalizedPath } = useShopI18n()
  const [state, dispatch] = useForgotPasswordFlowReducer()

  const viewModel = useForgotPasswordViewModel({
    state,
    t,
  })

  const fieldUi = buildFieldUiHelpers({
    fieldErrors: viewModel.fieldErrors,
    onFieldBlur: (field) => {
      if (field === 'email') {
        dispatch({ type: 'SET_EMAIL_TOUCHED', value: true })
      }
    },
  })

  const { handleSubmit } = useForgotPasswordSubmitHandlers({
    dispatch,
    email: state.email,
  })

  const loginHref = toLocalizedPath('/dashboard/login')

  return (
    <div className="ca-page ca-theme-transition py-6 sm:py-10 lg:py-14">
      <div className="mx-auto w-full max-w-[1320px] px-4 sm:px-6 lg:px-8">
        <section className="relative">
          <div className="relative grid items-start gap-y-7 gap-x-10 md:gap-x-12 lg:grid-cols-12 lg:gap-x-16 xl:gap-x-20">
            <div className="flex h-full flex-col gap-8 lg:col-span-7 lg:gap-10">
              <div className="space-y-3.5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-(--color-status-info)">
                  {t('common.customerArea')}
                </p>
                <h1 className="max-w-[20ch] text-balance text-3xl font-semibold tracking-tight text-(--color-text-primary) sm:text-4xl lg:text-[2.6rem]">
                  {viewModel.title}
                </h1>
                <p className="max-w-[62ch] text-sm leading-relaxed text-(--color-text-secondary) sm:text-base">
                  {viewModel.subtitle}
                </p>
              </div>
            </div>

            <div className="w-full lg:col-span-5 lg:justify-self-end lg:max-w-[29rem]">
              <div className="ca-card-shell ca-theme-transition relative overflow-hidden rounded-2xl border-[color-mix(in_srgb,var(--color-border-strong)_60%,var(--color-border-default))] bg-[color-mix(in_srgb,var(--color-bg-card)_94%,var(--color-bg-page-secondary))] px-4 py-4 shadow-[0_24px_48px_-30px_color-mix(in_srgb,var(--color-text-primary)_40%,transparent)] sm:px-5 sm:py-5">
                <CustomerAuthErrorBanner
                  error={state.error}
                  closeLabel={t('common.close')}
                  onClose={() => dispatch({ type: 'SET_ERROR', value: '' })}
                />

                {state.success ? (
                  <ForgotPasswordStateCard
                    hint={t('customer.auth.forgotPassword.successHint')}
                    actionLabel={t('customer.auth.forgotPassword.backToLogin')}
                    actionHref={loginHref}
                  />
                ) : (
                  <ForgotPasswordFormView
                    email={state.email}
                    submitting={state.submitting}
                    error={state.error}
                    loginHref={loginHref}
                    submitCta={t('customer.auth.forgotPassword.submitCta')}
                    submittingCta={t('customer.auth.forgotPassword.submittingCta')}
                    backToLoginLabel={t('customer.auth.forgotPassword.backToLogin')}
                    emailLabel={t('customer.auth.email')}
                    emailPlaceholder={t('customer.auth.emailPlaceholder')}
                    fieldUi={fieldUi}
                    onEmailChange={(value) => dispatch({ type: 'SET_EMAIL', value })}
                    onClearError={() => dispatch({ type: 'SET_ERROR', value: '' })}
                    onSubmit={handleSubmit}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}
